import * as React from 'react';
import { styled } from '@glitz/react';
import { ZIndex } from 'webapp/z-index';
import { translate, useMatchDevice } from '@avensia/excite';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { CloseIcon } from 'webapp/components/icons';
import { monochromeDark } from 'svenssons/shared/theme';
import { useOnClickOutside } from '@avensia/excite-ui';

type PropType = {
  show?: boolean;
  onClose: () => void;
  header?: string;
  parentElement?: HTMLDivElement | null;
};

const Modal = styled(
  ({ show = false, onClose, children, header, parentElement = null }: React.PropsWithChildren<PropType>) => {
    const modalRef = React.useRef<HTMLDivElement>(null);
    const deviceMatch = useMatchDevice();
    const isSmallDevice = deviceMatch.isMobile;
    let topPos = 0;
    if (parentElement && isSmallDevice) {
      topPos = parentElement.getBoundingClientRect().top + document.documentElement.scrollTop;
    }
    useOnClickOutside(modalRef, () => onClose());
    if (show) {
      return (
        <Container
          css={
            !isSmallDevice
              ? { width: '100%', maxWidth: '500px' }
              : { left: 15, right: 'unset', top: topPos, width: '80%' }
          }
          ref={modalRef}
        >
          <Wrapper>
            <Header>{header}</Header>
            <CloseButton
              as={Theme.Transparent}
              onClick={() => onClose()}
              square
              aria-label={translate(t => t.ariaLabelCloseButton)}
            >
              <CloseIcon />
            </CloseButton>
            <Content>{children}</Content>
          </Wrapper>
        </Container>
      );
    }
    return null;
  },
);

export default Modal;

const Container = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  boxShadow: 'rgba(0, 0, 0, 0.1) 10px 10px 40px 0px',
  zIndex: ZIndex.Modal,
  margin: { top: t => t.spacing.l },
});

const Header = styled.h1({
  fontSize: t => t.sharedFontSize.l,
  fontWeight: t => t.fontWeight?.normal,
  paddingTop: t => t.spacing.xs,
  gridArea: 'header',
  marginBottom: 'unset',
});

const Content = styled.div({
  paddingTop: t => t.spacing.xs,
  paddingBottom: t => t.spacing.s,
  gridArea: 'content',
  display: 'flex',
  width: '100%',
  wordWrap: 'break-word',
});

const Wrapper = styled.div({
  position: 'relative',
  padding: { x: t => t.sharedSpacing.m },
  backgroundColor: 'white',
  display: 'grid',
  width: '100%',
  gridTemplateRows: '1fr, 11fr',
  gridTemplateColumns: '100%',
  gridTemplateAreas: `
    'header'
    'content'
  `,
  border: { xy: { style: 'solid', color: monochromeDark, width: 1 } },
});

const CloseButton = styled(Button, {
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: ZIndex.Modal + 1,
});
